import Box from '@mui/material/Box';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DealDetailsPageHeader from '../components/DealDetailsPage/DealDetailsPageHeader';
import DealDetailsPageTable, {
  initialPageSetup,
} from '../components/DealDetailsPage/DealDetailsPageTable';
import { useDealDetailsQuery } from '../hooks/deal-details';
import { EDStatus, ExceptionBidStatus } from '../hooks/exceptionDeal';
import { useRoles } from '../../../provider/UserRoleProvider';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import { CenterLoader } from '@im/ui-lib/CenterLoader';
import { useStatusAlertAttrs } from './EDDealDetailsAlert';
import CustomErrorPage from '../../../components/CustomErrorPage/CustomErrorPage';
import { useTranslate } from '@im/react-localization';
import { DealDetailsTabsWrapper } from '../components/DealDetailsPage/DealDetailsTabsWrapper';
import EndUser from '../components/DealDetailsPage/EndUser/EndUser';
import { HorizontalTabsProps } from 'src/components/Tab/HorizontalTabs';

export default function EDDetailsPage(params: any) {
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get('countryCode');
  const companyCode = searchParams.get('companyCode');
  const businessType = searchParams.get('businessType');
  const importreferenceid = searchParams.get('importreferenceid');
  const customerset_id = searchParams.get('customerset_id');
  const dealid = searchParams.get('dealid');
  const isPurged = searchParams.get('isPurged')?.trim() === 'true';
  const navigate = useNavigate();
  const translate = useTranslate();
  const details = useDealDetailsQuery({
    CUSTOMERSET_ID: Number(customerset_id),
    dealid: Number(dealid),
    importreferenceid: Number(importreferenceid),
    isPennyCost: false,
    ...initialPageSetup,
    businessType: businessType,
    isPurged,
  });

  const dealStatus =
    details.data?.deal_id === Number(dealid) &&
    details.data?.customersetId === Number(customerset_id)
      ? details.data?.actualStatus
      : undefined;

  const { isCOGUser } = useRoles();
  const { alertColor, alertMessage, alertTitle, alertType } =
    useStatusAlertAttrs(
      isCOGUser,
      dealStatus as ExceptionBidStatus | undefined
    );

  const tabsData: HorizontalTabsProps['tabsData'] = [
    {
      tabContent: (
        <DealDetailsPageTable
          allowQuoteGeneration={
            !isCOGUser &&
            dealStatus === EDStatus.APPROVED &&
            details.data?.enable_Custom_OnDemand_Quote === 'Y'
          }
          companyCode={String(companyCode)}
          customerset_id={Number(customerset_id)}
          dealStatus={dealStatus}
          dealid={Number(dealid)}
          disableQtyEdit={
            details.data?.enable_Quote_QuantityEdit === 'N' ||
            details.data?.enable_Quote_QuantityEdit === ''
          }
          exception={true}
          importreferenceid={Number(importreferenceid)}
          vendorProgramId={Number(details.data?.vendor_program_id)}
        />
      ),
      tabTitle: translate(
        businessType === 'Subscription'
          ? 'VendorProgram.CloudProducts'
          : 'VendorDeals.DiscountLines',
        {
          count: details?.data?.lines_total_count,
        }
      ),
      tabValue:
        businessType === 'Subscription' ? 'cloudProducts' : 'discountLines',
    },
    {
      tabContent: <EndUser enduser={dealid ?? ''} isAffiliated={false} />,
      tabTitle: translate('EndUser.TabEndUsers'),
      tabValue: 'endUsers',
    },
  ];
  return (
    <>
      {details.isLoading ? (
        <CenterLoader />
      ) : (
        <>
          {alertType && !details.isError && (
            <Box display="flex" justifyContent="center">
              <Alert
                color={alertColor}
                icon={false}
                severity={alertType}
                sx={{ justifyContent: 'flex-start', width: '700px' }}
              >
                <AlertTitle>{alertTitle}</AlertTitle> {alertMessage}
              </Alert>
            </Box>
          )}
          <Box data-testid="gds-vp-ed-details-page" p={2}>
            {details.isError && (
              <CustomErrorPage
                errorText={translate('VendorProgram.CustomErrorText')}
                navigateBack={() => navigate(-1)}
              />
            )}
            {!details.isError && (
              <>
                <DealDetailsPageHeader
                  countryCode={String(countryCode)}
                  data={details.data}
                  dealStatus={dealStatus}
                  exception={true}
                  role={isCOGUser}
                />
                <DealDetailsTabsWrapper tabs={tabsData} />
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
}
